import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { DbEvent } from "@/types/database";
import { eventCategories, timeFilters } from "@/mocks/eventsData";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

interface RegistrationForm {
  name: string;
  email: string;
  phone: string;
  organization: string;
  notes: string;
}

type CategoryKey = string;
type TimeFilterKey = "upcoming" | "all" | "報名中" | "即將開始" | "名額有限" | "已結束";

export default function EventsList() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("all");
  const [activeTime, setActiveTime] = useState<TimeFilterKey>("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState<DbEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Registration modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<DbEvent | null>(null);
  const [formData, setFormData] = useState<RegistrationForm>({
    name: "",
    email: "",
    phone: "",
    organization: "",
    notes: "",
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    async function fetchEvents() {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true });
      if (!error && data) {
        setEvents(data as DbEvent[]);
      }
      setLoading(false);
    }
    fetchEvents();
  }, []);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      const matchCategory = activeCategory === "all" || e.category === activeCategory;

      let matchTime = true;
      if (activeTime === "upcoming") {
        matchTime = e.status !== "已結束";
      } else if (activeTime !== "all") {
        matchTime = e.status === activeTime;
      }

      const matchSearch =
        !searchQuery ||
        e.title.includes(searchQuery) ||
        (e.description && e.description.includes(searchQuery)) ||
        e.location.includes(searchQuery);

      return matchCategory && matchTime && matchSearch;
    });
  }, [activeCategory, activeTime, searchQuery, events]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (activeTime === "已結束" || activeTime === "all") {
      return list.sort((a, b) => b.date.localeCompare(a.date));
    }
    return list.sort((a, b) => a.date.localeCompare(b.date));
  }, [filtered, activeTime]);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "報名中":
        return "bg-navy-950 text-white";
      case "名額有限":
        return "bg-amber-600 text-white";
      case "即將開始":
        return "bg-silver-600 text-white";
      case "已結束":
        return "bg-silver-300 text-silver-700";
      default:
        return "bg-navy-950 text-white";
    }
  };

  function openRegisterModal(event: DbEvent) {
    setSelectedEvent(event);
    setFormData({ name: "", email: "", phone: "", organization: "", notes: "" });
    setFormSubmitted(false);
    setFormError("");
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setSelectedEvent(null);
  }

  async function handleRegisterSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedEvent) return;
    setFormError("");
    setFormSubmitting(true);

    try {
      const { error } = await supabase.from("event_registrations").insert([
        {
          event_id: selectedEvent.id,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          organization: formData.organization || null,
          notes: formData.notes || null,
          status: "registered",
        },
      ]);

      if (error) throw error;

      setFormSubmitted(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "報名失敗，請稍後再試";
      setFormError(message);
    } finally {
      setFormSubmitting(false);
    }
  }

  const timeActiveClass =
    "bg-navy-950 text-white shadow-lg shadow-navy-950/30 ring-2 ring-white/20";
  const timeInactiveClass =
    "bg-silver-50 text-silver-600 hover:bg-silver-100 border border-silver-200";
  const catActiveClass =
    "bg-navy-700 text-white shadow-lg shadow-navy-700/30 ring-2 ring-white/20";
  const catInactiveClass =
    "bg-white text-silver-600 hover:bg-silver-50 border border-silver-200";

  return (
    <section id="upcoming-events" className="py-24 md:py-32 bg-white relative">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Upcoming Events
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
              近期活動
            </h2>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-80">
            <Icon className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-silver-400" />
            <input
              type="text"
              placeholder="搜尋活動名稱或地點..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-silver-50 border border-silver-200 rounded-full text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
            />
          </div>
        </div>

        {/* Filter row: time + category */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-12 md:mb-16">
          {/* Time filter */}
          <div className="flex flex-wrap gap-2">
            {timeFilters.map((tf) => (
              <button
                key={`time-${tf.key}`}
                onClick={() => setActiveTime(tf.key as TimeFilterKey)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeTime === tf.key ? timeActiveClass : timeInactiveClass
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>

          <div className="hidden lg:block w-px h-6 bg-silver-200" />

          {/* Category filter */}
          <div className="flex flex-wrap gap-2">
            {eventCategories.map((cat) => (
              <button
                key={`cat-${cat.key}`}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeCategory === cat.key ? catActiveClass : catInactiveClass
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
            <p className="text-sm text-silver-400">載入活動中...</p>
          </div>
        ) : sorted.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {sorted.map((event) => (
              <article
                key={event.id}
                className="group bg-silver-50 rounded-3xl overflow-hidden border border-silver-200 hover:border-navy-200 hover:shadow-xl transition-all duration-500"
              >
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <SmartImage
                    src={event.image || ""}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    fallbackText={event.category}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/40 to-transparent" />

                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-navy-950/80 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full">
                      {event.category}
                    </span>
                    {event.is_online && (
                      <span className="bg-white/80 backdrop-blur-sm text-navy-800 text-xs font-medium px-3 py-1.5 rounded-full">
                        線上活動
                      </span>
                    )}
                  </div>
                  <span
                    className={`absolute top-4 right-4 text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm ${getStatusStyle(event.status)}`}
                  >
                    {event.status}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6 md:p-7">
                  <div className="flex items-center gap-3 text-sm text-silver-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Icon className="ri-calendar-line" />
                      {event.date}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-silver-300" />
                    <span className="flex items-center gap-1">
                      <Icon className="ri-map-pin-line" />
                      {event.location}
                    </span>
                    {event.capacity ? (
                      <>
                        <span className="w-1 h-1 rounded-full bg-silver-300" />
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Icon className="ri-user-line" />
                          名額 {event.capacity}
                        </span>
                      </>
                    ) : null}
                  </div>

                  <h3 className="text-lg font-semibold text-navy-950 mb-2 group-hover:text-navy-700 transition-colors leading-snug">
                    {event.title}
                  </h3>
                  <p className="text-sm text-silver-500 leading-relaxed line-clamp-2 mb-5">
                    {event.description || ""}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-silver-200">
                    <span className="text-sm font-medium text-navy-950 group-hover:text-navy-700 transition-colors inline-flex items-center gap-1 cursor-pointer">
                      瞭解詳情
                      <Icon className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </span>
                    {event.status === "報名中" || event.status === "名額有限" ? (
                      event.registration_url ? (
                        <a
                          href={event.registration_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-navy-950 text-white px-4 py-2 rounded-full font-medium hover:bg-navy-800 transition-colors whitespace-nowrap inline-flex items-center gap-1"
                        >
                          前往報名
                          <Icon className="ri-external-link-line" />
                        </a>
                      ) : (
                        <button
                          onClick={() => openRegisterModal(event)}
                          className="text-xs bg-navy-950 text-white px-4 py-2 rounded-full font-medium hover:bg-navy-800 transition-colors whitespace-nowrap"
                        >
                          立即報名
                        </button>
                      )
                    ) : event.status === "即將開始" ? (
                      <span className="text-xs bg-silver-200 text-silver-600 px-4 py-2 rounded-full font-medium whitespace-nowrap">
                        敬請期待
                      </span>
                    ) : (
                      <span className="text-xs bg-silver-200 text-silver-600 px-4 py-2 rounded-full font-medium whitespace-nowrap">
                        活動結束
                      </span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Icon className="ri-calendar-event-line text-5xl text-silver-300 mb-4" />
            <p className="text-silver-500 text-lg mb-2">沒有符合條件的活動</p>
            <p className="text-silver-400 text-sm">請嘗試其他篩選條件或搜尋關鍵字</p>
          </div>
        )}
      </div>

      {/* Registration Modal */}
      {showModal && selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {formSubmitted ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                  <Icon className="ri-check-line text-3xl text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-navy-950 mb-2">報名成功！</h3>
                <p className="text-sm text-silver-500 mb-6">
                  您已成功報名「{selectedEvent.title}」。我們將以 Email 發送活動詳情與提醒。
                </p>
                <button
                  onClick={closeModal}
                  className="bg-navy-950 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-navy-800 transition-colors"
                >
                  關閉
                </button>
              </div>
            ) : (
              <form onSubmit={handleRegisterSubmit} className="p-6 md:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-navy-950">活動報名</h3>
                    <p className="text-sm text-silver-500 mt-1">{selectedEvent.title}</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-silver-400 hover:bg-silver-100 hover:text-navy-700 transition-colors"
                  >
                    <Icon className="ri-close-line text-lg" />
                  </button>
                </div>

                {formError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2">
                    <Icon className="ri-error-warning-line" />
                    {formError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-navy-800 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-silver-50 border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                      placeholder="請輸入姓名"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-800 mb-1.5">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 bg-silver-50 border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-800 mb-1.5">電話</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 bg-silver-50 border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                        placeholder="09XX-XXX-XXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-800 mb-1.5">單位 / 組織</label>
                      <input
                        type="text"
                        value={formData.organization}
                        onChange={(e) => setFormData((prev) => ({ ...prev, organization: e.target.value }))}
                        className="w-full px-4 py-3 bg-silver-50 border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
                        placeholder="選填"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-800 mb-1.5">備註</label>
                    <textarea
                      rows={3}
                      maxLength={200}
                      value={formData.notes}
                      onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-silver-50 border border-silver-200 rounded-xl text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors resize-none"
                      placeholder="如有特殊需求或飲食限制請在此說明（選填，最多 200 字）"
                    />
                    <p className="text-xs text-silver-400 mt-1 text-right">最多 200 字</p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-silver-100 text-navy-800 py-3 rounded-full text-sm font-medium hover:bg-silver-200 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="flex-1 bg-navy-950 text-white py-3 rounded-full text-sm font-medium hover:bg-navy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  >
                    {formSubmitting ? (
                      <>
                        <Icon className="ri-loader-4-line animate-spin" />
                        送出中...
                      </>
                    ) : (
                      "確認報名"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}